/**
 * LayersPanel — draw.io-style layers manager.
 *
 * Lists layers (top row = front-most). Each row exposes:
 *   - a visibility (eye) toggle
 *   - a lock toggle
 *   - an inline-editable name
 *   - reorder up/down
 * Plus Add layer / Delete layer / set-Active controls.
 *
 * The panel is purely presentational — all state lives in the editor and is
 * mutated through the provided callbacks.
 */

import { useState } from 'react'
import {
  Eye,
  EyeOff,
  Lock,
  Unlock,
  ChevronUp,
  ChevronDown,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import type { DiagramLayer } from '../../types/schema'
import './LayersPanel.css'

interface LayersPanelProps {
  /** Layers in render order (index 0 = back-most). Displayed reversed so the
   *  front-most layer appears at the top, matching draw.io. */
  layers: DiagramLayer[]
  activeLayerId: string
  onSetActive: (id: string) => void
  onToggleVisible: (id: string) => void
  onToggleLocked: (id: string) => void
  onRename: (id: string, name: string) => void
  onMove: (id: string, direction: 'up' | 'down') => void
  onAdd: () => void
  onDelete: (id: string) => void
  onClose?: () => void
}

export default function LayersPanel({
  layers,
  activeLayerId,
  onSetActive,
  onToggleVisible,
  onToggleLocked,
  onRename,
  onMove,
  onAdd,
  onDelete,
  onClose,
}: LayersPanelProps) {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draftName, setDraftName] = useState('')

  // Display front-most (highest index) first.
  const ordered = [...layers].reverse()

  const startEdit = (layer: DiagramLayer) => {
    setEditingId(layer.id)
    setDraftName(layer.name)
  }

  const commitEdit = () => {
    if (editingId) {
      onRename(editingId, draftName.trim() || 'Layer')
    }
    setEditingId(null)
  }

  return (
    <div className="layers-panel">
      <div className="panel-header">
        <h3>Layers</h3>
        <div className="layers-header-actions">
          <button
            type="button"
            className="icon-btn"
            onClick={onAdd}
            title="Add layer"
          >
            <Plus size={16} />
          </button>
          {onClose && (
            <button
              type="button"
              className="icon-btn"
              onClick={onClose}
              title="Close layers"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      <div className="layers-list">
        {ordered.map((layer, displayIndex) => {
          const isActive = layer.id === activeLayerId
          // Index in the original (back-to-front) array.
          const realIndex = layers.length - 1 - displayIndex
          const isFront = realIndex === layers.length - 1
          const isBack = realIndex === 0
          return (
            <div
              key={layer.id}
              className={`layers-row${isActive ? ' active' : ''}`}
              onClick={() => onSetActive(layer.id)}
              title={isActive ? 'Active layer' : 'Click to set active'}
            >
              <button
                type="button"
                className="layers-toggle"
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleVisible(layer.id)
                }}
                title={layer.visible ? 'Hide layer' : 'Show layer'}
              >
                {layer.visible ? <Eye size={15} /> : <EyeOff size={15} />}
              </button>

              <button
                type="button"
                className={`layers-toggle${layer.locked ? ' locked' : ''}`}
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleLocked(layer.id)
                }}
                title={layer.locked ? 'Unlock layer' : 'Lock layer'}
              >
                {layer.locked ? <Lock size={15} /> : <Unlock size={15} />}
              </button>

              {editingId === layer.id ? (
                <input
                  className="layers-name-input"
                  value={draftName}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setDraftName(e.target.value)}
                  onBlur={commitEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitEdit()
                    if (e.key === 'Escape') setEditingId(null)
                  }}
                />
              ) : (
                <span
                  className="layers-name"
                  onDoubleClick={(e) => {
                    e.stopPropagation()
                    startEdit(layer)
                  }}
                >
                  {layer.name}
                </span>
              )}

              <div className="layers-row-actions">
                <button
                  type="button"
                  className="icon-btn"
                  disabled={isFront}
                  onClick={(e) => {
                    e.stopPropagation()
                    onMove(layer.id, 'up')
                  }}
                  title="Move up (forward)"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  disabled={isBack}
                  onClick={(e) => {
                    e.stopPropagation()
                    onMove(layer.id, 'down')
                  }}
                  title="Move down (back)"
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  type="button"
                  className="icon-btn"
                  disabled={layers.length <= 1}
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete(layer.id)
                  }}
                  title="Delete layer"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
